import { useCallback, useState } from 'react';

interface ToastItem {
    id: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info' | 'default';
    duration?: number;
    timestamp: number;
    onClose?: () => void;
}

export const useToast = () => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    let notificationId = 0;

    const generateId = useCallback(() => {
        return `toast-${++notificationId}-${Date.now()}`;
    }, []);


    const addToast = useCallback((
        message: string, 
        type: ToastItem['type'] = 'default',
        duration?: number,
        onClose?: () => void
    ) => {
        const id = generateId();
        
        // 기본 duration 설정 (모든 타입 1.5초 통일)
        let finalDuration: number;
        if (duration !== undefined) {
            finalDuration = duration;
        } else {
            finalDuration = 1500; // 모든 타입 1.5초
        }

        const newToast: ToastItem = {
            id,
            message,
            type,
            duration: finalDuration,
            timestamp: Date.now(),
            onClose
        };

        setToasts(prev => [...prev, newToast]);

        return id;
    }, [generateId]);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const clearAllToasts = useCallback(() => {
        setToasts([]);
    }, []);

    // 편의 메서드들
    const success = useCallback((message: string, duration?: number) => {
        return addToast(message, 'success', duration);
    }, [addToast]);

    const error = useCallback((message: string, duration?: number) => {
        return addToast(message, 'error', duration);
    }, [addToast]);

    const warning = useCallback((message: string, duration?: number) => {
        return addToast(message, 'warning', duration);
    }, [addToast]);

    const info = useCallback((message: string, duration?: number) => {
        return addToast(message, 'info', duration);
    }, [addToast]);

    return {
        toasts,
        addToast,
        removeToast,
        clearAllToasts,
        success,
        error,
        warning,
        info
    };
};